/**
 * #956 — keep the cached file lists in step with realtime `file:*` events for
 * as long as the app is signed in, whichever page is open.
 *
 * This is the ONE subscriber that patches the list caches from events.
 * `useFiles` used to do it, but only while a view using it was mounted: on the
 * Bin page (and any other page without one) an event arrived with nobody
 * listening, and a folder restored from the Bin stayed missing from Home's
 * cached root listing, which Home serves without refetching while it is fresh
 * (`staleTime` 30 s). `useFiles` now only keeps its own open-folder
 * accumulator in step.
 *
 * An event it can't patch (`patchListsFromFileEvent` → false) — a pointer row,
 * whose listing flags are per viewer, or one without its row — is not refetched
 * here: the lists of the folders it touched are only marked stale, so the next
 * view to read them fetches. Never `fileKeys.all` (the 30 s cache is
 * load-bearing for folder navigation).
 *
 * #1058: it also keeps every OTHER file cache in step the same way — search,
 * recently modified, breadcrumbs, the project lists and the Shared page
 * (`derived-cache-sync.ts`) — and marks them stale on a resync. Those used to
 * subscribe only while their own view was mounted, or not at all. The
 * launcher's grid, which lives in another feature, has its own sibling
 * (`LauncherCacheSync`, mounted beside this one).
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { onResyncBoundary } from '@papan/api-client'
import { useRealtime } from '@papan/realtime/client'
import { binNudgeSharedIn, changesViewerAccess, createAccessBurst, isBinNudge, isSharedPointerRow, type PointerKindRow } from '@papan/types'
import { onListedRowRefused } from '@papan/files/client'
import { fileKeys, findCachedListParents, normalizeEventParentId, noteAccessChanged, patchListsFromFileEvent } from '@papan/files/client/queries'
import { DerivedStaleBatch, applyBinNudgeToDerivedCaches, applyFileEventToDerivedCaches, flushDerivedCachesOnResync, markSharedPageStale, markSharedRowListsStale, refreshDerivedCachesForRefusedRow } from './derived-cache-sync'

// Built per call, never at module load: `@papan/files/ui` is imported by tests that mock `queries` partially.
const accessKeys = () => [[...fileKeys.all, 'permission'], [...fileKeys.all, 'list']] as const
// The details an access change can make wrong although the file's own permission answer may
// stay the same — so `readPermissionQuery`'s "re-read the detail when the answer changed"
// misses them: a `.shared` file's editor follows the SOURCE org's grant carried on the detail
// (`sourcePermission`), and a detail that FAILED (#630's "no longer available" tab) may now
// load. Every other detail is re-read only when its permission changed (#1259).
const accessDetails = () => ({
  queryKey: [...fileKeys.all, 'detail'],
  predicate: (q: { state: { status: string; data?: unknown } }) => {
    return q.state.status === 'error' || isSharedPointerRow(q.state.data as PointerKindRow | undefined)
  },
})
/** Mark what an access change can make wrong stale, with no request (#1260). */
function markFileCachesStale(queryClient: QueryClient): void {
  for (const queryKey of accessKeys()) void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
  void queryClient.invalidateQueries({ ...accessDetails(), refetchType: 'none' })
}

export function FileListCacheSync({ orgEmail }: {
  /** The active org email (`useTenant`), for an org-email switch within the tenant (#1210). */
  orgEmail?: string
} = {}): null {
  const queryClient = useQueryClient()
  const { onFileEvent, onResync, onOrgEvent, onPermissionChanged } = useRealtime()
  const [batch] = useState(() => new DerivedStaleBatch(queryClient))
  // App.tsx keys this component by tenant and auth state, so a switch or
  // sign-out unmounts it: what's pending is then applied as marks only, so
  // nothing refetches under the next scope (`DerivedStaleBatch.retire`).
  useEffect(() => () => batch.retire(), [batch])

  // #1264 — an action on a listed row was refused (403/404): its delete may never have been
  // announced (a crash between commit and publish; the owner chose no outbox — "loss is not lag").
  // The server can't say gone vs refused (no existence oracle), so the folder that LISTS the row is
  // re-read (on screen now, elsewhere on its next visit), and the other lists that can show it —
  // search, recent, the project lists, the Shared page. A row no cached list holds touches no folder
  // list. Never the row's own detail: re-reading it would refuse again. Fixes the row in whichever
  // org touches it — the owner org and every mounting org alike.
  // Batched: a bulk action refused for many rows (40 rows dragged into a read-only folder) re-reads
  // each folder once, after the burst — not once per row.
  useEffect(() => {
    const parents = new Set<string | null>()
    const rows = new Set<string>()
    let timer: ReturnType<typeof setTimeout> | null = null
    const flush = () => {
      timer = null
      for (const parentId of parents) void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', parentId] })
      refreshDerivedCachesForRefusedRow(queryClient, [...rows])
      parents.clear(); rows.clear()
    }
    const off = onListedRowRefused((fileId) => {
      for (const p of findCachedListParents(queryClient, fileId)) parents.add(p)
      rows.add(fileId)
      if (!timer) timer = setTimeout(flush, 150)
    })
    return () => { off(); if (timer) clearTimeout(timer) }
  }, [queryClient])

  useEffect(() => {
    return onFileEvent((event) => {
      // Bin-only fan-out: a source-tenant id that maps to nothing in the drive
      // cache — only the Shared page's broken states and, when tagged
      // `sharedIn: true`, this drive's own `.shared` rows can have changed (#1209 I3).
      if (isBinNudge(event)) {
        applyBinNudgeToDerivedCaches(queryClient, event, batch)
        if (binNudgeSharedIn(event) === true) markSharedRowListsStale(queryClient, batch)
        return
      }
      applyFileEventToDerivedCaches(queryClient, event, batch)
      if (patchListsFromFileEvent(queryClient, event)) return
      const parents = new Set<string | null>()
      for (const pid of [event.parentId, event.oldParentId]) {
        const p = normalizeEventParentId(pid)
        if (p !== undefined) parents.add(p)
      }
      for (const parentId of parents) {
        void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', parentId], refetchType: 'none' })
      }
    })
  }, [onFileEvent, queryClient, batch])

  // A grant change — a new outbound share, a revoke, incl. the cross-tenant
  // fan-out — has no file event behind it, and changes the Shared page.
  // An access or role change also invalidates the per-file permission cache and
  // the lists that seeded it (else a viewer just revoked from a folder would
  // still see "writable" for usePermission's 5-minute staleTime). Deliberately
  // on EVERY page, not only while a workspace is mounted: a drive-only tab's
  // permission cache needs it too.
  //
  // #1259 — this is the ONE re-read of the permission + list caches for an
  // access change (TabContent and EditorHost each used to re-read every open
  // file's detail and permission too):
  //  - at most once per interval (`createAccessBurst`);
  //  - not at all for a change that names ANOTHER member (`changesViewerAccess`);
  //  - every open file's permission in ONE request (`loadPermission` batches the
  //    refetches this starts), its detail only where that answer changed;
  //  - `permission:changed` too (a link minted / revoked, an app's grants, I116),
  //    which EditorHost used to re-read per tab.
  // #1260 — the caches are MARKED stale the moment a change arrives, and only the
  // refetch waits for the interval: a folder opened in between reads it fresh
  // (it used to be served from its 30 s cache for up to ~5 s).
  useEffect(() => {
    const markStale = () => markFileCachesStale(queryClient)
    const burst = createAccessBurst(() => {
      for (const queryKey of accessKeys()) void queryClient.invalidateQueries({ queryKey })
      void queryClient.invalidateQueries(accessDetails())
    })
    // reads already running predate the change: they seed nothing and are asked again (#1260)
    const changed = () => { noteAccessChanged(); markStale(); burst.push() }
    // A change naming ANOTHER member leaves this viewer's permissions as they were, but a listing
    // also carries who has access (the Access column): mark the lists stale — no request; the
    // folder on screen refreshes itself (DriveView), the rest on their next visit.
    const othersChanged = () => { void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list'], refetchType: 'none' }) }
    const offOrg = onOrgEvent((event) => {
      if (event.type === 'org:access_changed') markSharedPageStale(batch)
      if (event.type !== 'org:access_changed' && event.type !== 'org:role_changed') return
      if (changesViewerAccess(event)) changed(); else othersChanged()
    })
    const offPerm = onPermissionChanged((event) => { if (changesViewerAccess(event)) changed(); else othersChanged() })
    return () => {
      offOrg(); offPerm()
      // Unmounted mid-burst (tenant switch, sign-out): refetch nothing under the
      // next scope — already marked stale, so switching back reads fresh.
      burst.cancel()
    }
  }, [onOrgEvent, onPermissionChanged, queryClient, batch])

  // #1210 I3: an ORG-EMAIL switch within the tenant (no remount — this component is keyed by tenant) leaves every
  // per-identity key to re-key by itself, but a file's DETAIL key carries no identity: a `.shared` document's editor
  // decides read-only from its detail (`sourcePermission`), and a failed detail (#630) may now load. Re-read those —
  // the same details an access change re-reads. Compared with the last DEFINED email (X → none → Y is a switch). The
  // first email to land (page-load hydration) is not a switch — but a detail that FAILED before it may have been
  // refused for carrying no email (401 `org_email_required`), so failed details are re-read then too.
  // Accepted (scoped review): an open editor may still mount once on the old detail while its refetch is in flight —
  // one round trip; the server judges every write as the new identity.
  const lastOrgEmail = useRef(orgEmail)
  useEffect(() => {
    if (orgEmail === undefined) return
    const before = lastOrgEmail.current
    lastOrgEmail.current = orgEmail
    if (before === orgEmail) return
    if (before === undefined) {
      void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'detail'], predicate: (q: { state: { status: string } }) => q.state.status === 'error' })
      return
    }
    void queryClient.invalidateQueries(accessDetails())
  }, [orgEmail, queryClient])

  // #1210 (D1): a resync is an access-change boundary — events were missed while the socket was
  // down, access changes among them, so an answer asked before it may predate them. The realtime
  // client marks it ONCE, before any resync handler runs (`markResyncBoundary`), and this is what
  // the file caches do then: answers asked before it are asked again and seed nothing (#1260).
  useEffect(() => onResyncBoundary(noteAccessChanged), [])

  // …and, as for an access change, the permission and detail caches are re-read: a revoke missed
  // during the outage would otherwise stay on an open editor (5-minute permission `staleTime`).
  // Only those two — the lists are refetched by the resync handler below at once. Throttled like
  // an access change; dropped on unmount (already marked stale).
  const [resyncBurst] = useState(() => createAccessBurst(() => {
    void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'permission'] })
    void queryClient.invalidateQueries(accessDetails())
  }))
  useEffect(() => () => { resyncBurst.cancel() }, [resyncBurst])

  // Phase A.3 resync: the server says events were published while this
  // client's socket was gone, so any folder's cached list may hold rows that
  // no longer exist. Flush every list cache (the lists on screen refetch now,
  // the rest on their next visit). Per-key resync would need a deletion log
  // the seq counter doesn't expose; a reconnect is rare enough that one extra
  // round trip per folder is acceptable. `useFiles` reloads its own
  // open-folder accumulator on the same signal.
  useEffect(() => {
    return onResync((kind) => {
      if (kind !== 'file') return
      // the permission and detail marks (the lists are invalidated with a refetch just below)
      void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'permission'], refetchType: 'none' })
      void queryClient.invalidateQueries({ ...accessDetails(), refetchType: 'none' })
      resyncBurst.push()
      void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list'] })
      flushDerivedCachesOnResync(queryClient, batch)
    })
  }, [onResync, queryClient, batch, resyncBurst])

  return null
}

export { ACCESS_CHANGE_DELAY_MS, ACCESS_CHANGE_MIN_INTERVAL_MS } from '@papan/types'
